import { useEffect, useCallback, useState, useRef } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_HIGH,
  KEY_TAB_COMMAND,
} from 'lexical';
import {
  $isTableCellNode,
  $isTableRowNode,
  $isTableNode,
  TableCellNode,
  TableRowNode,
  $createTableCellNode,
  $createTableRowNode,
} from '@lexical/table';
import { $createParagraphNode } from 'lexical';
import './scss/TableEnhancements.scss';

function $getTableCellFromSelection(selection) {
  if (!$isRangeSelection(selection)) return null;
  const anchor = selection.anchor.getNode();
  let node = anchor;
  while (node) {
    if ($isTableCellNode(node)) return node;
    node = node.getParent();
  }
  return null;
}

function $getTableFromCell(cell) {
  let node = cell.getParent();
  while (node) {
    if ($isTableNode(node)) return node;
    node = node.getParent();
  }
  return null;
}

function $getRowFromCell(cell) {
  const parent = cell.getParent();
  return $isTableRowNode(parent) ? parent : null;
}

export default function TableEnhancementsPlugin() {
  const [editor] = useLexicalComposerContext();
  const [hoveredTable, setHoveredTable] = useState(null);
  const [tableRect, setTableRect] = useState(null);
  const containerRef = useRef(null);

  // Tab navigation between cells
  useEffect(() => {
    return editor.registerCommand(
      KEY_TAB_COMMAND,
      (event) => {
        const selection = $getSelection();
        const cell = $getTableCellFromSelection(selection);
        if (!cell) return false;

        event.preventDefault();
        const row = $getRowFromCell(cell);
        const table = $getTableFromCell(cell);
        if (!row || !table) return false;

        const rows = table.getChildren().filter($isTableRowNode);
        const cells = row.getChildren().filter($isTableCellNode);
        const cellIndex = cells.indexOf(cell);
        const rowIndex = rows.indexOf(row);

        if (event.shiftKey) {
          // Move to previous cell
          if (cellIndex > 0) {
            const prevCell = cells[cellIndex - 1];
            prevCell.selectEnd();
          } else if (rowIndex > 0) {
            const prevRow = rows[rowIndex - 1];
            const prevCells = prevRow.getChildren().filter($isTableCellNode);
            if (prevCells.length > 0) {
              prevCells[prevCells.length - 1].selectEnd();
            }
          }
        } else {
          // Move to next cell
          if (cellIndex < cells.length - 1) {
            const nextCell = cells[cellIndex + 1];
            nextCell.selectStart();
          } else if (rowIndex < rows.length - 1) {
            const nextRow = rows[rowIndex + 1];
            const nextCells = nextRow.getChildren().filter($isTableCellNode);
            if (nextCells.length > 0) {
              nextCells[0].selectStart();
            }
          } else {
            // Last cell of last row — add new row
            const colCount = cells.length;
            const newRow = $createTableRowNode();
            for (let i = 0; i < colCount; i++) {
              const newCell = $createTableCellNode(0);
              newCell.append($createParagraphNode());
              newRow.append(newCell);
            }
            table.append(newRow);
            const newCells = newRow.getChildren().filter($isTableCellNode);
            if (newCells.length > 0) {
              newCells[0].selectStart();
            }
          }
        }

        return true;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  // Add row to table
  const addRow = useCallback((tableKey, position = 'bottom') => {
    editor.update(() => {
      const table = editor.getEditorState().read(() => {
        // We need to get the node in the update context
      });
    });

    editor.update(() => {
      const editorState = editor.getEditorState();
      let tableNode = null;

      editorState.read(() => {
        const node = editor._editorState._nodeMap.get(tableKey);
        if (node && $isTableNode(node)) {
          tableNode = node;
        }
      });

      // Re-read within update
      const root = editor.getEditorState()._nodeMap;
      // Instead, just use the key-based approach
    });

    // Simpler approach: use editor.update directly
    editor.update(() => {
      // Find the table node by traversing
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const cell = $getTableCellFromSelection(selection);
      if (!cell) return;

      const table = $getTableFromCell(cell);
      if (!table) return;

      const rows = table.getChildren().filter($isTableRowNode);
      if (rows.length === 0) return;

      const colCount = rows[0].getChildren().filter($isTableCellNode).length;
      const newRow = $createTableRowNode();
      for (let i = 0; i < colCount; i++) {
        const newCell = $createTableCellNode(0);
        newCell.append($createParagraphNode());
        newRow.append(newCell);
      }

      if (position === 'bottom') {
        table.append(newRow);
      } else {
        const firstRow = rows[0];
        firstRow.insertBefore(newRow);
      }
    });
  }, [editor]);

  // Add column to table
  const addColumn = useCallback(() => {
    editor.update(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) return;

      const cell = $getTableCellFromSelection(selection);
      if (!cell) return;

      const table = $getTableFromCell(cell);
      if (!table) return;

      const rows = table.getChildren().filter($isTableRowNode);
      rows.forEach((row, index) => {
        const isHeader = index === 0;
        const newCell = $createTableCellNode(isHeader ? 1 : 0);
        newCell.append($createParagraphNode());
        row.append(newCell);
      });
    });
  }, [editor]);

  // Track hovered table for showing add buttons
  useEffect(() => {
    const rootElement = editor.getRootElement();
    if (!rootElement) return;

    const handleMouseMove = (e) => {
      const tableEl = e.target.closest('table');
      if (tableEl) {
        setHoveredTable(tableEl);
        const rect = tableEl.getBoundingClientRect();
        const rootRect = rootElement.getBoundingClientRect();
        setTableRect({
          top: rect.top - rootRect.top,
          left: rect.left - rootRect.left,
          width: rect.width,
          height: rect.height,
        });
      } else {
        setHoveredTable(null);
        setTableRect(null);
      }
    };

    const handleMouseLeave = () => {
      setHoveredTable(null);
      setTableRect(null);
    };

    rootElement.addEventListener('mousemove', handleMouseMove);
    rootElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      rootElement.removeEventListener('mousemove', handleMouseMove);
      rootElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [editor]);

  const handleAddRow = useCallback(() => {
    addRow(null, 'bottom');
  }, [addRow]);

  const handleAddColumn = useCallback(() => {
    addColumn();
  }, [addColumn]);

  if (!hoveredTable || !tableRect) return null;

  return (
    <div className="table-enhancements" ref={containerRef}>
      <button
        className="table-add-row-btn"
        style={{
          top: `${tableRect.top + tableRect.height}px`,
          left: `${tableRect.left}px`,
          width: `${tableRect.width}px`,
        }}
        onClick={handleAddRow}
        title="Add row"
        contentEditable={false}
      >
        <span>+</span>
      </button>
      <button
        className="table-add-col-btn"
        style={{
          top: `${tableRect.top}px`,
          left: `${tableRect.left + tableRect.width}px`,
          height: `${tableRect.height}px`,
        }}
        onClick={handleAddColumn}
        title="Add column"
        contentEditable={false}
      >
        <span>+</span>
      </button>
    </div>
  );
}
